import type { ClientHomePlanPublic } from "api-types/homePlans/homePlanResponses";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import { homePlanOverviewPublicPath, useAppRouterParams } from "lib/routeHelpers";
import { userCanMoveHomePlan } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteHomePlan } from "lib/storage/homePlanStorage";
import type { FormEvent } from "react";
import { useState } from "react";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import DropdownMenu, { DropdownMenuButton } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import useAppContext from "ui/hooks/useAppContext";
import { DeleteHomePlanMenuLink } from "./homePlanDisplayUtils";
import styles from "./HomePlanDropdown.module.scss";
import MoveHomePlanModal from "./moveHomePlans/MoveHomePlanModal";

interface PublicHomePlanDropdownProps {
  homePlan: ClientHomePlanPublic;
  onDelete: (id: string) => void;
}

export default function PublicHomePlanDropdown(props: PublicHomePlanDropdownProps) {
  const { builderId } = useAppRouterParams();
  const { user, builderUser, isSuperAdmin } = useUserContext();

  if (!builderId || !user || (!builderUser && !isSuperAdmin)) {
    return null;
  }

  return <ReadyPublicHomePlanDropdown builderId={builderId} props={props} />;
}

function ReadyPublicHomePlanDropdown({ builderId, props }: { builderId: string; props: PublicHomePlanDropdownProps }) {
  const { homePlan, onDelete } = props;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [movePlanModalOpen, setMovePlanModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { setUserNotification } = useAppContext();
  const userContext = useUserContext();
  const { hasPermission } = userContext;
  const sendHomePlanEvent = useHomePlanEvents(homePlan);

  const [canMoveHomePlan, cantMoveReason] = userCanMoveHomePlan(userContext, homePlan, "CUSTOMER_PLANS");

  const homePlanDeleteMessage = `Are you sure you want to delete ${homePlan.planName ?? "the Home Plan"}?`;

  const handleSubmitDelete = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsDeleting(true);

    const [, err] = await deleteHomePlan(builderId, homePlan.id);

    if (err) {
      setUserNotification(err);
      setIsDeleting(false);
      return;
    } else {
      sendHomePlanEvent("config:deleted_homeplan");
      onDelete(homePlan.id);
    }

    setIsDeleting(false);
    setDeleteModalOpen(false);
    setUserNotification({ message: `${homePlan.planName ?? "Home Plan"} deleted.`, messageType: "success" });
  };

  return (
    <div className={styles.wrapper}>
      <DropdownMenuButton open={menuOpen} handleClick={() => setMenuOpen(!menuOpen)} />
      {menuOpen && (
        <DropdownMenu onClose={() => setMenuOpen(false)}>
          <Link
            disabled={!canMoveHomePlan}
            title={cantMoveReason}
            onClick={() => {
              if (!canMoveHomePlan) {
                return;
              }
              setMenuOpen(false);
              setMovePlanModalOpen(true);
            }}
          >
            Move to a Location
          </Link>
          <Link href={homePlanOverviewPublicPath(builderId, homePlan.id)}>Preview Plan</Link>
          {hasPermission("DELETE_HOME_PLANS") && (
            <DeleteHomePlanMenuLink
              homePlan={homePlan}
              onClick={() => {
                setMenuOpen(false);
                setDeleteModalOpen(true);
              }}
            />
          )}
        </DropdownMenu>
      )}
      {deleteModalOpen && (
        <ConfirmDeleteModal
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleSubmitDelete}
          isDeleting={isDeleting}
          entityType="Home Plan"
          entityName={homePlan?.planName ?? "Home Plan"}
          message={homePlanDeleteMessage}
        />
      )}
      {movePlanModalOpen && <MoveHomePlanModal homePlan={homePlan} onClose={() => setMovePlanModalOpen(false)} />}
    </div>
  );
}
